/**
 * Proposta (`/propostas/:id`) — o documento gerado, somente leitura, fiel ao
 * layout impresso (`server/src/templates/proposal.js`, spec §7). Tudo o que o
 * documento mostra vem do *snapshot* gravado na proposta (`company`, `bank`,
 * `proposalDefaults`, itens) — nunca dos ajustes atuais, porque uma proposta
 * emitida não pode mudar de valor depois (ver docs/API.md).
 *
 * O documento é papel: fundo branco sempre, mesmo no tema escuro (por isso
 * usa `fmtBRL`/`fmtPct` diretamente em vez de `<Money>`/`<Percent>`, que
 * herdam cor dos tokens de tema — ver comentário na seção "DOCUMENTO").
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, apiUrl } from '@/lib/api';
import { fmtBRL, fmtDateTime, fmtPct, labelDocumento, PROPOSAL_STATUS_LABEL } from '@/lib/format';
import type { Proposal, ProposalStatus } from '@/lib/types';
import {
  useDeleteProposal,
  useDuplicateProposal,
  useProposal,
  useUpdateProposal,
} from '@/hooks/usePropostas';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner';
import './Proposta.css';

const STATUS_OPTIONS: ProposalStatus[] = ['rascunho', 'emitida', 'aceita', 'recusada', 'cancelada'];

export function Proposta() {
  const { id } = useParams();
  const numericId = id && /^\d+$/.test(id) ? Number(id) : undefined;
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const proposalQuery = useProposal(numericId);
  const updateProposal = useUpdateProposal();
  const deleteProposal = useDeleteProposal();
  const duplicateProposal = useDuplicateProposal();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [printing, setPrinting] = useState(false);

  if (numericId === undefined) {
    return (
      <EmptyState
        title="Proposta não encontrada"
        description="O link usado não é válido."
        action={
          <Link className="btn btn-primary" to="/propostas">
            Voltar ao histórico
          </Link>
        }
      />
    );
  }

  if (proposalQuery.isLoading) {
    return <Spinner label="Carregando proposta…" />;
  }

  if (proposalQuery.isError || !proposalQuery.data) {
    return (
      <EmptyState
        title="Proposta não encontrada"
        description="Ela pode ter sido excluída, ou o link está incorreto."
        action={
          <Link className="btn btn-primary" to="/propostas">
            Voltar ao histórico
          </Link>
        }
      />
    );
  }

  const proposal = proposalQuery.data;
  const canDelete = !!user && (user.id === proposal.userId || user.role === 'admin');

  async function handleStatusChange(next: ProposalStatus) {
    if (next === proposal.status) return;
    try {
      await updateProposal.mutateAsync({ id: proposal.id, input: { status: next } });
      toast.success(`Status atualizado para ${PROPOSAL_STATUS_LABEL[next]}.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível atualizar o status agora.');
    }
  }

  async function handleConfirmDuplicate() {
    try {
      const { proposal: created, skipped } = await duplicateProposal.mutateAsync(proposal.id);
      setConfirmDuplicate(false);
      if (skipped.length > 0) {
        toast.info(
          `Proposta ${created.code} criada com os preços de hoje. ${skipped.length} equipamento(s) não puderam ser copiados: ` +
            `${skipped.map((s) => s.name).join(', ')}.`,
          { duration: 9000 }
        );
      } else {
        toast.success(`Proposta ${created.code} criada a partir desta, com os preços de hoje.`);
      }
      navigate(`/propostas/${created.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível duplicar a proposta agora.');
    }
  }

  async function handleConfirmDelete() {
    try {
      await deleteProposal.mutateAsync(proposal.id);
      toast.success('Proposta excluída.');
      navigate('/propostas');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível excluir a proposta agora.');
      setConfirmDelete(false);
    }
  }

  async function handlePrint() {
    setPrinting(true);
    try {
      const res = await fetch(apiUrl(`/proposals/${proposal.id}/pdf`), { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      if (res.status === 503) {
        toast.info(
          'Geração de PDF indisponível neste servidor agora — abrindo a versão para impressão do navegador.'
        );
        window.open(apiUrl(`/proposals/${proposal.id}/print`), '_blank', 'noopener');
        return;
      }
      let message = 'Não foi possível gerar o PDF agora.';
      try {
        const body = (await res.json()) as { error?: { message?: string } };
        if (body?.error?.message) message = body.error.message;
      } catch {
        // resposta sem corpo JSON — mantém a mensagem padrão
      }
      toast.error(message);
    } catch {
      toast.error('Não foi possível conectar ao servidor para gerar o PDF. Tente de novo.');
    } finally {
      setPrinting(false);
    }
  }

  return (
    <div className="proposta-page">
      <div className="proposta-toolbar no-print">
        <Button variant="ghost" onClick={() => navigate('/propostas')}>
          ← Voltar
        </Button>

        <div className="row row-wrap proposta-toolbar__actions">
          <div className="field proposta-status">
            <label className="field-label" htmlFor="proposta-status">
              Status
            </label>
            <select
              id="proposta-status"
              className="select"
              value={proposal.status}
              disabled={updateProposal.isPending}
              onChange={(e) => handleStatusChange(e.target.value as ProposalStatus)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {PROPOSAL_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <Button variant="ghost" onClick={() => setConfirmDuplicate(true)}>
            Duplicar
          </Button>
          {canDelete ? (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Excluir
            </Button>
          ) : null}
          <Button onClick={handlePrint} disabled={printing}>
            {printing ? 'Preparando…' : 'Imprimir / salvar PDF'}
          </Button>
        </div>
      </div>

      <p className="proposta-note no-print">
        Elaborada por {proposal.seller.name} em {fmtDateTime(proposal.createdAt)} — os valores abaixo são os
        registrados no dia da emissão, mesmo que o catálogo ou os ajustes tenham mudado desde então.
      </p>

      <Documento proposal={proposal} />

      <ConfirmDialog
        open={confirmDuplicate}
        title="Duplicar proposta"
        message="Cria uma nova proposta com o mesmo cliente e os mesmos equipamentos, repreçada pelos preços vigentes hoje — o total pode ficar diferente do desta proposta. Equipamentos sem preço vigente hoje não entram na cópia."
        confirmLabel={duplicateProposal.isPending ? 'Duplicando…' : 'Duplicar'}
        loading={duplicateProposal.isPending}
        onCancel={() => setConfirmDuplicate(false)}
        onConfirm={handleConfirmDuplicate}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir proposta"
        message={`Excluir a proposta ${proposal.code}? Esta ação não pode ser desfeita.`}
        tone="danger"
        confirmLabel="Excluir"
        loading={deleteProposal.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

// -------------------------------------------------------------------- documento --

/**
 * O documento propriamente dito — papel, não interface. Usa `fmtBRL`/`fmtPct`
 * (funções puras) em vez de `<Money>`/`<Percent>` de propósito: aqueles
 * componentes herdam cor dos tokens de tema (`--ink-faint` etc.), que mudam
 * no escuro; o papel não pode mudar de aparência com o tema do app.
 */
function Documento({ proposal }: { proposal: Proposal }) {
  const company = proposal.company;
  const bank = proposal.bank;
  const defaults = proposal.proposalDefaults;
  const seller = proposal.seller;

  return (
    <div className="sheet">
      <div className="sheet-head">
        <div>
          <h1 className="display sheet-head__title">Proposta comercial</h1>
          <p className="mono sheet-head__code">{proposal.code}</p>
        </div>
        <div className="sheet-head__meta">
          <p>Emitida em {fmtDateTime(proposal.createdAt)}</p>
          <p>Validade: {proposal.validadeDias} dias</p>
        </div>
      </div>

      <div className="parties">
        <div className="party">
          <h3>De</h3>
          <p className="party__name">{company.razaoSocial}</p>
          <dl>
            <div>
              <b>CNPJ</b> {company.cnpj}
            </div>
            <div>{company.endereco}</div>
            <div>
              <b>Contato</b> {seller.name}
              {seller.phone ? ` · ${seller.phone}` : ''}
            </div>
            {seller.email ? <div>{seller.email}</div> : null}
          </dl>
        </div>
        <div className="party">
          <h3>Para</h3>
          <p className="party__name">{proposal.clientRazaoSocial}</p>
          <dl>
            <div>
              <b>{labelDocumento(proposal.clientCnpj)}</b> {proposal.clientCnpj}
            </div>
            {proposal.clientContato ? (
              <div>
                <b>Contato</b> {proposal.clientContato}
              </div>
            ) : null}
            {proposal.clientCelular ? <div>{proposal.clientCelular}</div> : null}
            {proposal.clientEmail ? <div>{proposal.clientEmail}</div> : null}
            <div>
              <b>Estado de destino</b> {proposal.estadoNome}
            </div>
          </dl>
        </div>
      </div>

      <table className="doc-table">
        <thead>
          <tr>
            <th>Qtd.</th>
            <th>Equipamento</th>
            <th className="dt-right">Vl. unitário</th>
            <th className="dt-right">IPI</th>
            <th className="dt-right">Vl. total</th>
          </tr>
        </thead>
        <tbody>
          {proposal.items.map((it) => (
            <tr key={it.id}>
              <td className="mono">{it.qty}</td>
              <td>
                <p className="dt-name">{it.name}</p>
                {it.details ? <p className="dt-sub">{it.details}</p> : null}
                {it.accessories ? <p className="dt-sub">{it.accessories}</p> : null}
              </td>
              <td className="dt-right mono">{fmtBRL(it.unitValue)}</td>
              <td className="dt-right mono">{fmtPct(it.ipi)}</td>
              <td className="dt-right mono">{fmtBRL(it.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="totals-block">
        <div className="tb-row">
          <span>Subtotal produtos + IPI</span>
          <span className="mono">{fmtBRL(proposal.subtotal)}</span>
        </div>
        <div className="tb-row">
          <span>ICMS DIFAL (recolhido antecipadamente pela Arkmeds)</span>
          <span className="mono">{fmtBRL(proposal.totalDifal)}</span>
        </div>
        <div className="tb-row">
          <span>FCP (recolhido antecipadamente pela Arkmeds)</span>
          <span className="mono">{fmtBRL(proposal.totalFCP)}</span>
        </div>
        <div className="tb-row tb-row--grand">
          <span>Total geral</span>
          <span className="mono">{fmtBRL(proposal.totalGeral)}</span>
        </div>
      </div>

      <div className="doc-2col">
        <div className="doc-block">
          <h4>Condições de pagamento</h4>
          <ul>
            {(defaults.condicoesPagamento ?? []).map((c, i) => (
              <li key={i}>
                {c.titulo ? <b>{c.titulo}</b> : null}
                {c.titulo && c.descricao ? ' — ' : ''}
                {c.descricao}
              </li>
            ))}
          </ul>
        </div>
        <div className="doc-block">
          <h4>Dados bancários</h4>
          <p>
            {bank.banco}
            <br />
            Agência: {bank.agencia} · Conta: {bank.conta}
            <br />
            CNPJ: {company.cnpj}
            <br />
            Pix: {bank.pix}
          </p>
        </div>
      </div>

      <div className="doc-block doc-block--observacoes">
        <h4>Observações</h4>
        <ul>
          {(defaults.observacoes ?? []).map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      </div>

      <div className="disclaimer">{defaults.disclaimer}</div>

      <p className="ncm-note mono">Equipamentos adquiridos a título de ativo imobilizado.</p>
      <p className="ncm-note ncm-note-tight mono">
        NCM utilizado na produção: {company.ncm} · Elaborado por {seller.name || '—'}
      </p>
    </div>
  );
}
